/**
 * React Query hooks for domains.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { domainApi } from '@/api/client';
import type { DomainCreate } from '@/api/schemas';

export function useDomains() {
  return useQuery({
    queryKey: ['domains'],
    queryFn: () => domainApi.listDomains(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useDomainStatus(domainId: string) {
  return useQuery({
    queryKey: ['domain-status', domainId],
    queryFn: () => domainApi.getDomainStatus(domainId),
    enabled: !!domainId,
    refetchInterval: 10000, // Poll every 10 seconds
  });
}

export function useCreateDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: DomainCreate) => domainApi.createDomain(data),
    onSuccess: async () => {
      // Invalidate domains list and usage stats
      await queryClient.invalidateQueries({ queryKey: ['domains'] });
      await queryClient.invalidateQueries({ queryKey: ['usage'] });
    },
  });
}

export function useVerifyDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (domainId: string) => domainApi.getDomainStatus(domainId),
    onSuccess: async (data, domainId) => {
      // Invalidate domain status and domains list to refresh verification state
      await queryClient.invalidateQueries({ queryKey: ['domain-status', domainId] });
      await queryClient.invalidateQueries({ queryKey: ['domains'] });
      
      // If verified, refetch to get updated domain data
      if (data.verified) {
        await queryClient.refetchQueries({ queryKey: ['domains'] });
      }
    },
  });
}

export function useDeleteDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (domainId: string) => domainApi.deleteDomain(domainId),
    // Optimistic update: remove domain from cache immediately
    onMutate: async (domainId) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ['domains'] });
      await queryClient.cancelQueries({ queryKey: ['usage'] });
      
      // Snapshot the previous values
      const previousDomains = queryClient.getQueryData<{ domains: Array<{ domain_id: string }> }>(['domains']);
      const previousUsage = queryClient.getQueryData<{ usage: { domains: number; links: number; clicks: number }; limits: { domains: number; links: number; clicks: number }; plan: string }>(['usage']);
      
      // Optimistically update to remove the domain
      if (previousDomains) {
        queryClient.setQueryData(['domains'], {
          ...previousDomains,
          domains: previousDomains.domains.filter(d => d.domain_id !== domainId),
        });
      }
      
      // Optimistically decrease domain count in usage
      if (previousUsage && previousUsage.usage.domains > 0) {
        queryClient.setQueryData(['usage'], {
          ...previousUsage,
          usage: {
            ...previousUsage.usage,
            domains: Math.max(0, previousUsage.usage.domains - 1),
          },
        });
      }
      
      // Return context with snapshots for rollback
      return { previousDomains, previousUsage };
    },
    onSuccess: async () => {
      // Refetch domains and usage to ensure we have the latest data from server
      await queryClient.refetchQueries({ 
        queryKey: ['domains'],
        type: 'active'
      });
      await queryClient.refetchQueries({ 
        queryKey: ['usage'],
        type: 'active'
      });
    },
    onError: (error, domainId, context) => {
      // Rollback to previous state on error
      if (context?.previousDomains) {
        queryClient.setQueryData(['domains'], context.previousDomains);
      }
      if (context?.previousUsage) {
        queryClient.setQueryData(['usage'], context.previousUsage);
      }
      console.error('Failed to delete domain:', error);
    },
  });
}

